import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { loadFromSupabase } from "../supabaseDB";
import SearchSelect from "./SearchSelect";
import ExportButton from "./ExportButton";
import { num2, intOr, s as xs } from "./xlsxExport";
import {
  defaultOpenSeasonIds,
  buildSeasonFilterOptions,
  seasonFilterPlaceholder,
  loadSeasons,
  resolveSeasonId,
} from "./seasonFilter";

/* ============================================================
   CombinedWeeklySummary — Client Pricing & POs · Tab 3
   ------------------------------------------------------------
   Read-only consolidated view. Merges rows from
     - gfi-fixed-pos     (Fixed Pricing — Tab 1, Four Seasons-style)
     - gfi-consignment-pos (Consignment — Tab 2, Twin River-style)
   into a single time-ordered table grouped by ISO week, with
   subtotal rows at the bottom of each week group.

   This module is INTENTIONALLY view-only. There is no edit/delete
   UI here — the source tabs own the data. Clicking a row shows a
   guidance toast pointing back to the source tab. The selection
   checkboxes drive the standard aggregate bar at the top.

   Data is read fresh from localStorage on every render — any
   change in Tab 1 or Tab 2 surfaces here on next mount without
   any sync wiring.
   ============================================================ */

const T = {
  en: {
    title: "Combined Weekly Summary", subtitle: "Consolidated view of Fixed + Consignment sales by week (read-only)",
    readOnlyMsg: "This module is view-only. Edit data in Fixed Pricing or Consignment Pricing.",
    /* Cards */
    cardRevenue: "Total Revenue", cardCases: "Total Cases",
    cardEstProfit: "Est. Profit", cardRealProfit: "Real Profit",
    cardFixed: "Fixed Sales", cardConsignment: "Consignment Sales",
    /* Columns */
    colDate: "Departure\nDate", colDelivery: "Loading/\nDelivery Date", colWeek: "Wk", colClient: "Client", colType: "Type",
    colProduct: "Product", colCat: "CAT",
    colPO: "PO #", colPU: "PU #",
    colPallets: "Pallets", colCases: "Cases",
    colSalePrice: "Sale\nPrice", colGrowerPrice: "Grower\nPrice",
    colRevenue: "Revenue",
    colDiffProfit: "Diff\nProfit", colCommProfit: "Comm\nProfit",
    colSLProfit: "SL\nProfit",
    colFinanceProfit: "Finance\nProfit",
    colEstProfit: "Est.\nProfit", colRealProfit: "Real\nProfit",
    colSaleStatus: "Sale\nStatus",
    /* Filters */
    search: "Search by PO / PU / Product / Client...",
    allClients: "All clients", allProducts: "All products",
    allCategories: "All categories", allTypes: "All types",
    allStatuses: "All statuses", allWeeks: "All weeks",
    typeFixed: "Fixed", typeConsignment: "Consignment",
    /* Status */
    statusPending: "Pending", statusInvoiced: "Invoiced",
    statusDraft: "Draft", statusSent: "Sent", statusPaid: "Paid",
    statusGoodDelivery: "Good Delivery", statusProtection: "Protection", statusPAS: "PAS",
    statusSettled: "Settled", statusDumped: "Dumped", statusDonated: "Donated", statusSamples: "Samples", statusTransfer: "Transfer",
    /* Subtotal */
    weekSubtotal: "Week", weekSubtotalSuffix: "Subtotal",
    weekHeader: "Week",
    noResults: "No rows match the filters.",
    noData: "No data to display.",
    noDataHint: "Capture POs with PO #, product, prices and cases in Fixed Pricing or Consignment Pricing.",
    weekShort: "W",
  },
  es: {
    title: "Resumen Semanal Combinado", subtitle: "Vista consolidada de ventas Fijas + Consignación por semana (solo lectura)",
    readOnlyMsg: "Este módulo es solo de consulta. Edita los datos en Fixed Pricing o Consignment Pricing.",
    cardRevenue: "Venta Total", cardCases: "Cajas Totales",
    cardEstProfit: "Ganancia Estimada", cardRealProfit: "Ganancia Real",
    cardFixed: "Ventas Fijas", cardConsignment: "Ventas en Consignación",
    colDate: "Fecha\nSalida", colDelivery: "Carga/\nEntrega", colWeek: "Sem", colClient: "Cliente", colType: "Tipo",
    colProduct: "Producto", colCat: "CAT",
    colPO: "PO #", colPU: "PU #",
    colPallets: "Pallets", colCases: "Cajas",
    colSalePrice: "Precio\nVenta", colGrowerPrice: "Precio\nExp.",
    colRevenue: "Venta",
    colDiffProfit: "Gan.\nDifer.", colCommProfit: "Gan.\nCom.",
    colSLProfit: "Gan.\nSL",
    colFinanceProfit: "Gan.\nFin.",
    colEstProfit: "Gan.\nEst.", colRealProfit: "Gan.\nReal",
    colSaleStatus: "Status\nVenta",
    search: "Buscar por PO / PU / Producto / Cliente...",
    allClients: "Todos los clientes", allProducts: "Todos los productos",
    allCategories: "Todas las categorías", allTypes: "Todos los tipos",
    allStatuses: "Todos los estados", allWeeks: "Todas las semanas",
    typeFixed: "Fijo", typeConsignment: "Consignación",
    statusPending: "Pendiente", statusInvoiced: "Facturado",
    statusDraft: "Borrador", statusSent: "Enviado", statusPaid: "Pagado",
    statusGoodDelivery: "Good Delivery", statusProtection: "Protección", statusPAS: "PAS",
    statusSettled: "Liquidado", statusDumped: "Dumped", statusDonated: "Donated", statusSamples: "Samples", statusTransfer: "Transferencia",
    weekSubtotal: "Semana", weekSubtotalSuffix: "Subtotal",
    weekHeader: "Semana",
    noResults: "Ninguna fila coincide con los filtros.",
    noData: "No hay datos para mostrar.",
    noDataHint: "Captura POs con PO #, producto, precios y cajas en Fixed Pricing o Consignment Pricing.",
    weekShort: "Sem",
  },
};

/* ---------- Constants ---------- */
const DEFAULT_COMM_GFI = 5;
const DEFAULT_COMM_TR = 10;
/* Must match STATUS_COLORS in FixedPricingRegistry / ConsignmentPricingRegistry —
   when a PO is linked to an invoice the row inherits the invoice's status
   (draft / sent / paid), so those keys need entries here or the badge
   falls back to gray. */
const STATUS_COLORS = {
  pending:      { bg: "rgba(156,163,175,0.15)", text: "#6B7280", border: "rgba(156,163,175,0.3)" },
  draft:        { bg: "rgba(107,114,128,0.12)", text: "#4B5563", border: "rgba(107,114,128,0.3)" },
  sent:         { bg: "rgba(245,158,11,0.15)",  text: "#B45309", border: "rgba(245,158,11,0.3)" },
  paid:         { bg: "rgba(16,185,129,0.15)",  text: "#047857", border: "rgba(16,185,129,0.3)" },
  invoiced:     { bg: "rgba(59,130,246,0.15)",  text: "#2563EB", border: "rgba(59,130,246,0.3)" },
  goodDelivery: { bg: "rgba(34,197,94,0.15)",   text: "#15803D", border: "rgba(34,197,94,0.3)" },
  protection:   { bg: "rgba(245,158,11,0.15)",  text: "#B45309", border: "rgba(245,158,11,0.3)" },
  pas:          { bg: "rgba(217,70,239,0.15)",  text: "#A21CAF", border: "rgba(217,70,239,0.3)" },
  settled:      { bg: "rgba(16,185,129,0.15)",  text: "#047857", border: "rgba(16,185,129,0.3)" },
  dumped:       { bg: "rgba(220,38,38,0.15)",   text: "#B91C1C", border: "rgba(220,38,38,0.3)" },
  donated:      { bg: "rgba(20,184,166,0.15)",  text: "#0F766E", border: "rgba(20,184,166,0.3)" },
  samples:      { bg: "rgba(168,85,247,0.15)",  text: "#7E22CE", border: "rgba(168,85,247,0.3)" },
  transfer:     { bg: "rgba(59,130,246,0.15)",  text: "#2563EB", border: "rgba(59,130,246,0.3)" },
};

const inp = { padding: "7px 10px", fontSize: 12, border: "1px solid var(--color-border)", borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

/* ---------- Storage helpers ---------- */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch (e) { return fb; } }
function getFixedPOs()       { return loadLS("gfi-fixed-pos", []); }
function getConsignmentPOs() { return loadLS("gfi-consignment-pos", []); }
function getInvoices()       { return loadLS("gfi-invoices", []); }

/* ISO-8601 week number — same algorithm used in ShipmentRegistry /
   FixedPricingRegistry / ConsignmentPricingRegistry. The combined
   view recomputes the week from the Loading/Delivery Date so it
   stays correct even for legacy records that stored the week based
   on the departure date. */
function getISOWeek(dateStr) {
  if (!dateStr) return "";
  var d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  var target = new Date(d.valueOf());
  var dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  var firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) target.setMonth(0, 1 + ((4 - target.getDay()) + 7) % 7);
  return 1 + Math.ceil((firstThursday - target) / 604800000);
}

function fmtMoney(n) {
  var v = parseFloat(n);
  if (isNaN(v)) return "—";
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtMoneyShort(n) {
  var v = parseFloat(n);
  if (isNaN(v) || v === 0) return "—";
  /* Thousand separators applied app-wide for consistency. */
  return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ---------- Source normalizers ----------
   Each tab has a different shape; both collapse here into a single
   row contract so the table + aggregator stay simple. The unified
   row contains every column the table renders, the Real-vs-Est
   profit pair, the source type, and a stable composite id that
   prefixes "F:" or "C:" so selection works across both sources
   without id collisions. */

/* Completeness gate — a row only surfaces in the combined view when
   it carries enough captured data to be meaningful:
     - a date (otherwise it can't be slotted into a week group)
     - a PO number
     - a product description
     - at least one positive case count
     - at least one priced field (the per-source price below)
   Half-typed drafts in Tab 1/Tab 2 stay invisible here until the
   user finishes them. */
function fixedRowIsComplete(line) {
  if (!line.date) return false;
  if (!String(line.poNumber || "").trim()) return false;
  if (!(line.productDescription || line.productBase || line.productName)) return false;
  var cases = parseInt(line.casesReceived, 10);
  if (!cases || cases <= 0) cases = parseInt(line.casesSent, 10) || 0;
  if (cases <= 0) return false;
  /* At least the FS price (the sale price the client pays) must be set. */
  if (!parseFloat(line.priceFS)) return false;
  return true;
}
function consignmentRowIsComplete(line) {
  if (!line.date) return false;
  if (!String(line.poNumber || "").trim()) return false;
  if (!(line.productDescription || line.productBase || line.productName)) return false;
  if (!(parseInt(line.cases, 10) > 0)) return false;
  /* Range pricing — both Min and Max TR must be present to count. */
  if (!parseFloat(line.minTR)) return false;
  if (!parseFloat(line.maxTR)) return false;
  return true;
}

function normalizeFixedRow(line) {
  var cases = parseInt(line.casesReceived, 10);
  if (!cases || cases <= 0) cases = parseInt(line.casesSent, 10) || 0;
  var hasReal = line.priceFSReal !== "" && line.priceFSReal != null && !isNaN(parseFloat(line.priceFSReal));
  var fsEst = parseFloat(line.priceFS) || 0;
  var fsReal = hasReal ? parseFloat(line.priceFSReal) : fsEst;
  var sl = parseFloat(line.priceSL) || 0;
  var gf = parseFloat(line.priceGF) || 0;
  var grower = parseFloat(line.priceGrower) || 0;
  var commGFI = parseFloat(line.commissionPct); if (isNaN(commGFI)) commGFI = DEFAULT_COMM_GFI;
  /* Components — SL Profit is unique to Fixed (FS−SL spread). */
  var profitSL_est   = (fsEst  - sl) * cases;
  var profitSL_real  = (fsReal - sl) * cases;
  var profitDiff     = (gf - grower) * cases;
  var profitComm     = grower * (commGFI / 100) * cases;
  /* Finance markup — SL charges GF financeFeeCharged %, pays AGROCAP
     financeFeePaid %. Spread × invoice value rolls into the total.
     Est. uses the FS estimate; Real swaps in the invoice-supplied FS. */
  var finPaid       = parseFloat(line.financeFeePaid) || 0;
  var finCharged    = parseFloat(line.financeFeeCharged) || 0;
  var invoiceValueEst  = fsEst  * cases;
  var invoiceValueReal = fsReal * cases;
  var profitFinance_est  = invoiceValueEst  * ((finCharged - finPaid) / 100);
  var profitFinance_real = invoiceValueReal * ((finCharged - finPaid) / 100);
  /* Week follows the Loading/Delivery Date — recomputed here so the
     column stays correct even for legacy rows that stored week from
     the departure date. */
  var weekFromDelivery = line.deliveryDate ? getISOWeek(line.deliveryDate) : (line.week || "");
  return {
    id: "F:" + line.id,
    sourceType: "fixed",
    date: line.date || "",
    deliveryDate: line.deliveryDate || "",
    week: weekFromDelivery,
    clientName: line.clientName || "",
    productDescription: line.productDescription || line.productBase || line.productName || "",
    category: line.category || "",
    poNumber: line.poNumber || "",
    puNumber: line.puNumber || "",
    pallets: parseInt(line.palletsSent, 10) || 0,
    cases: cases,
    salePriceEst: fsEst,
    salePriceReal: fsReal,
    growerPrice: grower,
    revenueEst:  fsEst  * cases,
    revenueReal: fsReal * cases,
    profitSL:        profitSL_est,
    profitSL_real:   profitSL_real,
    profitDiff: profitDiff,
    profitComm: profitComm,
    profitFinance:      profitFinance_est,
    profitFinance_real: profitFinance_real,
    estProfit:  profitSL_est  + profitDiff + profitComm + profitFinance_est,
    realProfit: hasReal ? (profitSL_real + profitDiff + profitComm + profitFinance_real) : 0,
    hasReal: hasReal,
    saleStatus: line.saleStatus || "pending",
    linkedInvoiceId: line.linkedInvoiceId || "",
    seasonId: line.seasonId || "",
  };
}

function normalizeConsignmentRow(line) {
  var cases   = parseInt(line.cases, 10) || 0;
  var minTR   = parseFloat(line.minTR) || 0;
  var maxTR   = parseFloat(line.maxTR) || 0;
  var commTR  = parseFloat(line.commTR); if (isNaN(commTR)) commTR = DEFAULT_COMM_TR;
  var diff    = parseFloat(line.differential) || 0;
  var commGFI = parseFloat(line.commGFI); if (isNaN(commGFI)) commGFI = DEFAULT_COMM_GFI;
  var trMult  = 1 - (commTR / 100);
  var midTR = (minTR + maxTR) / 2;
  var midGrower = (midTR * trMult) - diff;
  var hasReal = line.priceRealTR !== "" && line.priceRealTR != null && !isNaN(parseFloat(line.priceRealTR));
  var realTR = hasReal ? parseFloat(line.priceRealTR) : 0;
  var realGrower = hasReal ? (realTR * trMult) - diff : 0;
  /* Differential is the per-line absolute spread × cases (same for
     estimated and real — captured up front). */
  var profitDiff = diff * cases;
  var estProfitComm  = midGrower  * (commGFI / 100) * cases;
  var realProfitComm = hasReal ? realGrower * (commGFI / 100) * cases : 0;
  /* Finance markup mirrors the Fixed path. Consignment lines don't
     currently carry these fields so the helper falls back to 0 — no
     behavior change until the columns are added on that side. Est.
     uses the mid-TR sale value; Real swaps in the invoice-supplied
     TR price. */
  var cFinPaid    = parseFloat(line.financeFeePaid) || 0;
  var cFinCharged = parseFloat(line.financeFeeCharged) || 0;
  var consInvoiceValueEst  = midTR  * cases;
  var consInvoiceValueReal = hasReal ? realTR * cases : midTR * cases;
  var consProfitFinance_est  = consInvoiceValueEst  * ((cFinCharged - cFinPaid) / 100);
  var consProfitFinance_real = consInvoiceValueReal * ((cFinCharged - cFinPaid) / 100);
  /* Week follows the Loading/Delivery Date — see note in
     normalizeFixedRow. */
  var weekFromDelivery = line.deliveryDate ? getISOWeek(line.deliveryDate) : (line.week || "");
  return {
    id: "C:" + line.id,
    sourceType: "consignment",
    date: line.date || "",
    deliveryDate: line.deliveryDate || "",
    week: weekFromDelivery,
    clientName: line.clientName || "",
    productDescription: line.productDescription || line.productBase || line.productName || "",
    category: line.category || "",
    poNumber: line.poNumber || "",
    puNumber: line.puNumber || "",
    pallets: parseInt(line.pallets, 10) || 0,
    cases: cases,
    salePriceEst: midTR,
    salePriceReal: hasReal ? realTR : midTR,
    growerPrice: hasReal ? realGrower : midGrower,
    revenueEst:  midTR * cases,
    revenueReal: hasReal ? realTR * cases : midTR * cases,
    /* TR consignment has no SweetLeaf spread — column shows 0. */
    profitSL: 0,
    profitSL_real: 0,
    profitDiff: profitDiff,
    profitComm: hasReal ? realProfitComm : estProfitComm,
    profitFinance:      consProfitFinance_est,
    profitFinance_real: consProfitFinance_real,
    estProfit:  profitDiff + estProfitComm + consProfitFinance_est,
    realProfit: hasReal ? (profitDiff + realProfitComm + consProfitFinance_real) : 0,
    hasReal: hasReal,
    saleStatus: line.saleStatus || "pending",
    linkedInvoiceId: line.linkedInvoiceId || "",
    seasonId: line.seasonId || "",
  };
}

/* ---------- Inline UI ---------- */
function StatusBadge({ status, t }) {
  var c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  var label = t["status" + (status ? status.charAt(0).toUpperCase() + status.slice(1) : "Pending")] || status;
  return <span style={{ padding: "2px 7px", borderRadius: 10, fontSize: 9, fontWeight: 700, background: c.bg, color: c.text, border: "1px solid " + c.border, whiteSpace: "nowrap", display: "inline-block" }}>{label}</span>;
}
function TypeBadge({ type, t }) {
  var isFixed = type === "fixed";
  var bg = isFixed ? "rgba(59,130,246,0.12)" : "rgba(245,158,11,0.12)";
  var text = isFixed ? "#2563EB" : "#B45309";
  var border = isFixed ? "rgba(59,130,246,0.3)" : "rgba(245,158,11,0.3)";
  var label = isFixed ? t.typeFixed : t.typeConsignment;
  return <span style={{ padding: "2px 7px", borderRadius: 10, fontSize: 9, fontWeight: 700, background: bg, color: text, border: "1px solid " + border, whiteSpace: "nowrap", display: "inline-block" }}>{label}</span>;
}
function Toast({ msg, onDismiss }) {
  var [fading, setFading] = useState(false);
  useEffect(function () {
    if (!msg) return;
    setFading(false);
    var fadeT = setTimeout(function () { setFading(true); }, 2700);
    var closeT = setTimeout(onDismiss, 3000);
    return function () { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [msg, onDismiss]);
  if (!msg) return null;
  return createPortal(<div style={{ position: "fixed", bottom: 24, right: 24, padding: "10px 18px", background: "var(--color-accent)", color: "var(--color-accent-text)", borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 999999, maxWidth: 360, animation: fading ? "fadeOutCorner 0.3s ease forwards" : undefined }}>{msg}</div>, document.body);
}

/* ======================== MAIN ======================== */
export default function CombinedWeeklySummary({ lang: globalLang }) {
  var lang = globalLang || "es"; var t = T[lang];
  /* `reloadKey` bumps when the user wants a fresh pull from
     localStorage (e.g., after switching tabs externally). */
  var [reloadKey, setReloadKey] = useState(0);
  /* Preload from Supabase so the legacy `gfi-*` caches that
     `getFixedPOs()` / `getConsignmentPOs()` / `getInvoices()` read are
     fresh — re-runs on focus and bumps `reloadKey` once the cache is
     warm. */
  useEffect(function () {
    var alive = true;
    var preload = function () {
      var pairs = [
        ["fixed_pos", "gfi-fixed-pos"],
        ["consignment_pos", "gfi-consignment-pos"],
        ["invoices", "gfi-invoices"],
      ];
      return Promise.all(pairs.map(function (p) {
        return loadFromSupabase(p[0]).then(function (rows) {
          try { localStorage.setItem(p[1], JSON.stringify(rows)); } catch (e) {}
        }).catch(function () {});
      })).then(function () { if (alive) setReloadKey(function (k) { return k + 1; }); });
    };
    preload();
    var onFocus = function () { preload(); };
    window.addEventListener("focus", onFocus);
    return function () { alive = false; window.removeEventListener("focus", onFocus); };
  }, []);

  /* Source data — read fresh, merge, normalize. Half-typed drafts
     (missing date / PO# / product / cases / required price) are
     filtered out BEFORE normalization so the table never shows
     orphan rows or weird "Week ?" subtotals. Re-runs when
     reloadKey ticks (focus event) so external edits from the other
     tabs surface on tab switch. */
  var rawRows = useMemo(function () {
    var fixed = getFixedPOs().filter(fixedRowIsComplete).map(normalizeFixedRow);
    var cons  = getConsignmentPOs().filter(consignmentRowIsComplete).map(normalizeConsignmentRow);
    return fixed.concat(cons);
  }, [reloadKey]);

  /* Invoice status overlay — same pattern Tab 1/Tab 2 use. */
  var invoicesById = useMemo(function () {
    var out = {};
    getInvoices().forEach(function (inv) { if (inv.id != null) out[String(inv.id)] = inv; });
    return out;
  }, [reloadKey]);
  var enriched = useMemo(function () {
    return rawRows.map(function (r) {
      if (!r.linkedInvoiceId) return r;
      var inv = invoicesById[String(r.linkedInvoiceId)];
      if (!inv) return r;
      var invStatus = inv.saleStatus || inv.status || r.saleStatus;
      return { ...r, saleStatus: invStatus };
    });
  }, [rawRows, invoicesById]);

  var seasons = useMemo(function () { return loadSeasons(); }, [reloadKey]);

  /* Filter / sort state */
  var [search, setSearch] = useState("");
  var [filterClient, setFilterClient]   = useState("");
  var [filterProduct, setFilterProduct] = useState("");
  var [filterCategory, setFilterCategory] = useState("");
  var [filterType, setFilterType]       = useState("");
  var [filterStatus, setFilterStatus]   = useState("");
  var [filterWeek, setFilterWeek]       = useState("");
  var [filterSeasons, setFilterSeasons] = useState(function () { return defaultOpenSeasonIds(); });
  var [dateFrom, setDateFrom] = useState("");
  var [dateTo, setDateTo]     = useState("");
  var [cardFilter, setCardFilter] = useState("");
  var [selected, setSelected] = useState({});
  var [sortCol, setSortCol] = useState("date");
  var [sortDir, setSortDir] = useState("desc");
  var [toast, setToast] = useState("");

  /* Unique filter dropdown values pulled from the data set. */
  var uniqueClients   = useMemo(function () { return [...new Set(enriched.map(function (r) { return r.clientName; }).filter(Boolean))].sort(); }, [enriched]);
  var uniqueProducts  = useMemo(function () { return [...new Set(enriched.map(function (r) { return r.productDescription; }).filter(Boolean))].sort(); }, [enriched]);
  var uniqueWeeks     = useMemo(function () { return [...new Set(enriched.map(function (r) { return r.week; }).filter(Boolean))].sort(function (a, b) { return parseInt(b, 10) - parseInt(a, 10); }); }, [enriched]);
  var uniqueStatuses  = useMemo(function () { return [...new Set(enriched.map(function (r) { return r.saleStatus; }).filter(Boolean))]; }, [enriched]);

  var seasonFilteredRows = useMemo(function () {
    if (filterSeasons.length === 0) return enriched;
    return enriched.filter(function (r) {
      var sid = resolveSeasonId(seasons, r.date, r.seasonId);
      return sid && filterSeasons.indexOf(String(sid)) >= 0;
    });
  }, [enriched, filterSeasons, seasons]);

  var filtered = useMemo(function () {
    var list = seasonFilteredRows.slice();
    if (search) {
      var q = search.toLowerCase();
      list = list.filter(function (r) {
        return (r.poNumber || "").toLowerCase().includes(q)
          || (r.puNumber || "").toLowerCase().includes(q)
          || (r.productDescription || "").toLowerCase().includes(q)
          || (r.clientName || "").toLowerCase().includes(q);
      });
    }
    if (filterClient)   list = list.filter(function (r) { return r.clientName === filterClient; });
    if (filterProduct)  list = list.filter(function (r) { return r.productDescription === filterProduct; });
    if (filterCategory) list = list.filter(function (r) { return r.category === filterCategory; });
    if (filterType)     list = list.filter(function (r) { return r.sourceType === filterType; });
    if (filterStatus)   list = list.filter(function (r) { return (r.saleStatus || "pending") === filterStatus; });
    if (filterWeek)     list = list.filter(function (r) { return String(r.week) === String(filterWeek); });
    if (dateFrom) list = list.filter(function (r) { return r.date && r.date >= dateFrom; });
    if (dateTo)   list = list.filter(function (r) { return r.date && r.date <= dateTo; });
    /* Card filters — same semantic as the cards themselves. */
    if (cardFilter === "fixed")       list = list.filter(function (r) { return r.sourceType === "fixed"; });
    if (cardFilter === "consignment") list = list.filter(function (r) { return r.sourceType === "consignment"; });
    if (cardFilter === "real")        list = list.filter(function (r) { return r.hasReal; });
    return list;
  }, [seasonFilteredRows, search, filterClient, filterProduct, filterCategory, filterType, filterStatus, filterWeek, dateFrom, dateTo, cardFilter]);

  var sorted = useMemo(function () {
    var list = filtered.slice();
    list.sort(function (a, b) {
      var va, vb;
      switch (sortCol) {
        case "date":       va = a.date || "";       vb = b.date || ""; break;
        case "delivery":   va = a.deliveryDate || ""; vb = b.deliveryDate || ""; break;
        case "week":       va = parseInt(a.week, 10) || 0; vb = parseInt(b.week, 10) || 0; break;
        case "client":     va = (a.clientName || "").toLowerCase(); vb = (b.clientName || "").toLowerCase(); break;
        case "type":       va = a.sourceType || ""; vb = b.sourceType || ""; break;
        case "product":    va = (a.productDescription || "").toLowerCase(); vb = (b.productDescription || "").toLowerCase(); break;
        case "po":         va = a.poNumber || "";   vb = b.poNumber || ""; break;
        case "cases":      va = a.cases || 0;       vb = b.cases || 0; break;
        case "revenue":    va = (a.hasReal ? a.revenueReal : a.revenueEst); vb = (b.hasReal ? b.revenueReal : b.revenueEst); break;
        case "estProfit":  va = a.estProfit || 0;   vb = b.estProfit || 0; break;
        case "realProfit": va = a.realProfit || 0;  vb = b.realProfit || 0; break;
        case "status":     va = a.saleStatus || ""; vb = b.saleStatus || ""; break;
        default: va = ""; vb = "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, sortCol, sortDir]);

  /* Week-grouping removed per spec — the table renders rows flat.
     Aggregate totals are exposed through the summary cards above
     and the MODULE TOTAL block in the selection bar; no in-table
     subtotal or header rows. */

  /* Aggregator used by both the selection bar and module total. */
  var aggregateRows = useCallback(function (list) {
    var out = { count: 0, cases: 0, revenue: 0, profitDiff: 0, profitComm: 0, profitSL: 0, profitFinance: 0, estProfit: 0, realProfit: 0 };
    (list || []).forEach(function (r) {
      out.count        += 1;
      out.cases        += r.cases || 0;
      out.revenue      += (r.hasReal ? r.revenueReal : r.revenueEst) || 0;
      out.profitDiff   += r.profitDiff || 0;
      out.profitComm   += r.profitComm || 0;
      out.profitSL     += (r.hasReal ? r.profitSL_real : r.profitSL) || 0;
      out.profitFinance+= (r.hasReal ? r.profitFinance_real : r.profitFinance) || 0;
      out.estProfit    += r.estProfit || 0;
      out.realProfit   += r.realProfit || 0;
    });
    return out;
  }, []);

  var selectedRows  = useMemo(function () { return filtered.filter(function (r) { return selected[r.id]; }); }, [filtered, selected]);
  /* Export rows — selected if any checked, else full filtered set.
     One row per line, columns matching the consolidated table
     (Loading/Delivery first, Departure second, then the unified
     pricing + profit columns). The Type column flags source so the
     spreadsheet stays meaningful when re-sorted. */
  var buildExportRows = function () {
    var src = selectedRows.length > 0 ? selectedRows : filtered;
    return src.map(function (r) {
      return {
        "Loading/Delivery Date": xs(r.deliveryDate),
        "Departure Date": xs(r.date),
        "Week": xs(r.week ? "W" + r.week : ""),
        "Client": xs(r.clientName),
        "Type": r.sourceType === "fixed" ? "Fixed" : "Consignment",
        "Product": xs(r.productDescription),
        "Category": xs(r.category),
        "PO #": xs(r.poNumber),
        "PU #": xs(r.puNumber),
        "Pallets": intOr(r.pallets),
        "Cases": intOr(r.cases),
        "Sale Price": num2(r.hasReal ? r.salePriceReal : r.salePriceEst),
        "Grower Price": num2(r.growerPrice),
        "Revenue": num2(r.hasReal ? r.revenueReal : r.revenueEst),
        "Diff Profit": num2(r.profitDiff),
        "Comm Profit": num2(r.profitComm),
        "SL Profit": r.sourceType === "fixed" ? num2(r.hasReal ? r.profitSL_real : r.profitSL) : 0,
        "Finance Profit": num2(r.hasReal ? r.profitFinance_real : r.profitFinance),
        "Est. Profit": num2(r.estProfit),
        "Real Profit": r.hasReal ? num2(r.realProfit) : "",
        "Sale Status": xs(r.saleStatus),
      };
    });
  };
  var selectedTotals = useMemo(function () { return aggregateRows(selectedRows); }, [selectedRows, aggregateRows]);
  var grandTotals    = useMemo(function () { return aggregateRows(filtered); }, [filtered, aggregateRows]);
  var clearSelection = function () { setSelected({}); };
  var selectedCount = selectedRows.length;

  /* Summary card totals split by sourceType so the Fixed /
     Consignment cards can show counts AND money in one number. */
  /* Reads from seasonFilteredRows (not `filtered`) so clicking the
     fixed/consignment/real cards doesn't collapse the OTHER cards'
     counts against themselves — same isolation pattern as
     BillRegistry/InvoiceRegistry/FixedPricingRegistry/
     ConsignmentPricingRegistry. */
  var stats = useMemo(function () {
    var revenue = 0, cases = 0, estProfit = 0, realProfit = 0;
    var fixedCount = 0, fixedRevenue = 0;
    var consCount = 0, consRevenue = 0;
    seasonFilteredRows.forEach(function (r) {
      var rev = r.hasReal ? r.revenueReal : r.revenueEst;
      revenue += rev;
      cases += r.cases;
      estProfit += r.estProfit;
      realProfit += r.realProfit;
      if (r.sourceType === "fixed") { fixedCount++; fixedRevenue += rev; }
      else { consCount++; consRevenue += rev; }
    });
    return { revenue: revenue, cases: cases, estProfit: estProfit, realProfit: realProfit, fixedCount: fixedCount, fixedRevenue: fixedRevenue, consCount: consCount, consRevenue: consRevenue };
  }, [seasonFilteredRows]);

  var toggleSort = function (col) {
    if (sortCol === col) { setSortDir(sortDir === "asc" ? "desc" : "asc"); return; }
    setSortCol(col); setSortDir("desc");
  };
  var arrow = function (col) {
    if (sortCol !== col) return <span style={{ marginLeft: 3, color: "var(--color-text-muted)", opacity: 0.4, fontSize: 9 }}>↕</span>;
    return <span style={{ marginLeft: 3, color: "var(--color-accent)", fontSize: 9 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  };

  /* Standard money cell — positive green, negative red parens. */
  var moneyCell = function (n, opts) {
    var v = parseFloat(n) || 0;
    var neg = v < 0;
    var color = neg ? "#DC2626" : (v > 0 ? (opts && opts.emphasize ? "var(--color-accent)" : "#047857") : "var(--color-text-muted)");
    var txt = neg ? "(" + fmtMoney(Math.abs(v)) + ")" : fmtMoney(v);
    return <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: color }}>{txt}</span>;
  };

  /* Row click — guidance only, since the module is read-only. */
  var showReadOnlyMsg = function () { setToast(t.readOnlyMsg); };

  /* Compact mode — `\n` in column labels renders as a real 2-line
     wrap (whiteSpace: pre-line) so wide tables fit on screen
     without horizontal scroll. */
  var th  = { padding: "5px 4px", textAlign: "left",  fontWeight: 700, fontSize: 9, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", background: "var(--color-bg)", position: "sticky", top: 0, zIndex: 1, cursor: "pointer", whiteSpace: "pre-line", lineHeight: 1.15, verticalAlign: "bottom", borderBottom: "2px solid var(--color-border)" };
  var thR = { ...th, textAlign: "right" };
  var thC = { ...th, textAlign: "center" };
  var td  = { padding: "5px 4px", fontSize: 10, borderBottom: "1px solid var(--color-border)", whiteSpace: "nowrap" };
  var tdR = { ...td, textAlign: "right", fontFamily: "'JetBrains Mono', monospace" };
  var tdC = { ...td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace" };

  var renderCard = function (key, label, value, color) {
    var active = cardFilter === key;
    return (
      <div key={key} onClick={function () { setCardFilter(active ? "" : key); }} style={{ padding: 12, borderRadius: 8, border: "1px solid " + (active ? color : "var(--color-border)"), background: active ? "rgba(42,122,111,0.05)" : "var(--color-bg-card)", cursor: "pointer", transition: "all 0.15s" }}>
        <div style={{ fontSize: 10, color: color, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: color, marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
      </div>
    );
  };

  if (rawRows.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
        </div>
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--color-text-muted)", border: "1px solid var(--color-border)", borderRadius: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-secondary)" }}>{t.noData}</div>
          <div style={{ fontSize: 12, marginTop: 6 }}>{t.noDataHint}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{t.subtitle}</div>
        </div>
        <ExportButton lang={lang} filenameBase="Combined_Weekly_Summary" selectedCount={selectedRows.length} totalCount={filtered.length} buildSummary={buildExportRows} buildDetail={buildExportRows} />
      </div>

      {/* Summary cards — clickable to filter */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 14 }}>
        {renderCard("revenue",      t.cardRevenue,    fmtMoney(stats.revenue),         "var(--color-accent)")}
        {renderCard("cases",        t.cardCases,      stats.cases.toLocaleString(),    "#2563EB")}
        {renderCard("estProfit",    t.cardEstProfit,  fmtMoney(stats.estProfit),       "#B45309")}
        {renderCard("real",         t.cardRealProfit, fmtMoney(stats.realProfit),      "#047857")}
        {renderCard("fixed",        t.cardFixed,      stats.fixedCount + " · " + fmtMoneyShort(stats.fixedRevenue), "#1F4E79")}
        {renderCard("consignment",  t.cardConsignment,stats.consCount + " · " + fmtMoneyShort(stats.consRevenue),   "#D97706")}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input value={search} onChange={function (e) { setSearch(e.target.value); }} placeholder={t.search} style={{ ...inp, maxWidth: 260, flex: "1 1 200px" }} />
        <div style={{ minWidth: 160 }}><SearchSelect value={filterClient} onChange={setFilterClient} placeholder={t.allClients} options={uniqueClients.map(function (s) { return { value: s, label: s }; })} /></div>
        <div style={{ minWidth: 200 }}><SearchSelect value={filterProduct} onChange={setFilterProduct} placeholder={t.allProducts} options={uniqueProducts.map(function (s) { return { value: s, label: s }; })} /></div>
        <div style={{ minWidth: 130 }}><SearchSelect value={filterCategory} onChange={setFilterCategory} placeholder={t.allCategories} options={[{ value: "OG", label: "OG" }, { value: "CV", label: "CV" }]} /></div>
        <div style={{ minWidth: 130 }}><SearchSelect value={filterType} onChange={setFilterType} placeholder={t.allTypes} options={[{ value: "fixed", label: t.typeFixed }, { value: "consignment", label: t.typeConsignment }]} /></div>
        <div style={{ minWidth: 120 }}><SearchSelect value={filterWeek} onChange={setFilterWeek} placeholder={t.allWeeks} options={uniqueWeeks.map(function (w) { return { value: String(w), label: "W" + w }; })} /></div>
        <div style={{ minWidth: 160 }}><SearchSelect value={filterStatus} onChange={setFilterStatus} placeholder={t.allStatuses} options={uniqueStatuses.map(function (s) { return { value: s, label: t["status" + s.charAt(0).toUpperCase() + s.slice(1)] || s }; })} /></div>
        <div style={{ minWidth: 180 }}><SearchSelect multi value={filterSeasons} onChange={setFilterSeasons} placeholder={seasonFilterPlaceholder(lang)} groups={buildSeasonFilterOptions(lang)} /></div>
        <input type="date" value={dateFrom} onChange={function (e) { setDateFrom(e.target.value); }} style={{ ...inp, maxWidth: 140 }} title={lang === "es" ? "Desde" : "From"} />
        <input type="date" value={dateTo}   onChange={function (e) { setDateTo(e.target.value); }} style={{ ...inp, maxWidth: 140 }} title={lang === "es" ? "Hasta" : "To"} />
        {(dateFrom || dateTo || filterClient || filterProduct || filterCategory || filterType || filterStatus || filterWeek || cardFilter || search) && (
          <button onClick={function () { setSearch(""); setFilterClient(""); setFilterProduct(""); setFilterCategory(""); setFilterType(""); setFilterStatus(""); setFilterWeek(""); setDateFrom(""); setDateTo(""); setCardFilter(""); }} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
        )}
      </div>

      {/* Selection summary bar — same shape as Fixed / Consignment.
          No Delete button — module is read-only. */}
      {selectedCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(42,122,111,0.06)", border: "1px solid rgba(42,122,111,0.15)", marginBottom: 10, fontSize: 12 }}>
          <button onClick={clearSelection} style={{ padding: "6px 12px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{lang === "es" ? "Limpiar" : "Clear"}</button>
          <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 14, flexWrap: "wrap", fontSize: 11 }}>
            <span style={{ fontWeight: 700, color: "var(--color-accent)" }}>{selectedCount} {lang === "es" ? "seleccionados" : "selected"}</span>
            <span style={{ color: "var(--color-text-muted)" }}>|</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colRevenue}: </span>{moneyCell(selectedTotals.revenue)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colCases}: </span><span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{selectedTotals.cases.toLocaleString()}</span></span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colDiffProfit}: </span>{moneyCell(selectedTotals.profitDiff)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colCommProfit}: </span>{moneyCell(selectedTotals.profitComm)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colFinanceProfit}: </span>{moneyCell(selectedTotals.profitFinance)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colSLProfit}: </span>{moneyCell(selectedTotals.profitSL)}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colEstProfit}: </span>{moneyCell(selectedTotals.estProfit, { emphasize: true })}</span>
            <span><span style={{ color: "var(--color-text-muted)" }}>{t.colRealProfit}: </span>{moneyCell(selectedTotals.realProfit, { emphasize: true })}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, minWidth: 260, paddingLeft: 14, borderLeft: "1px dashed var(--color-border)" }}>
            <div style={{ fontWeight: 700, color: "var(--color-text-secondary)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.06em" }}>{lang === "es" ? "Total módulo" : "Module total"}</div>
            <div style={{ display: "flex", gap: 8, fontSize: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <span>{moneyCell(grandTotals.revenue)}</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-text)" }}>{grandTotals.cases.toLocaleString()}c</span>
              <span>{moneyCell(grandTotals.profitDiff)}</span>
              <span>{moneyCell(grandTotals.profitComm)}</span>
              <span>{moneyCell(grandTotals.profitFinance)}</span>
              <span>{moneyCell(grandTotals.profitSL)}</span>
              <span>{moneyCell(grandTotals.estProfit, { emphasize: true })}</span>
              <span>{moneyCell(grandTotals.realProfit, { emphasize: true })}</span>
            </div>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--color-text-muted)", fontSize: 14, border: "1px solid var(--color-border)", borderRadius: 10 }}>{t.noResults}</div>
      ) : (
        <div style={{ overflowX: "auto", maxHeight: "calc(100vh - 360px)", borderRadius: 10, border: "1px solid var(--color-border)" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...thC, width: 32 }}>
                  <input type="checkbox" checked={sorted.length > 0 && sorted.every(function (r) { return selected[r.id]; })} onChange={function (e) {
                    var next = { ...selected };
                    if (e.target.checked) sorted.forEach(function (r) { next[r.id] = true; });
                    else sorted.forEach(function (r) { delete next[r.id]; });
                    setSelected(next);
                  }} />
                </th>
                {/* Date columns per spec: Loading/Delivery, Departure, Week. */}
                <th onClick={function () { toggleSort("delivery"); }} style={th}>{t.colDelivery}{arrow("delivery")}</th>
                <th onClick={function () { toggleSort("date"); }} style={th}>{t.colDate}{arrow("date")}</th>
                <th onClick={function () { toggleSort("week"); }} style={thC}>{t.colWeek}{arrow("week")}</th>
                <th onClick={function () { toggleSort("client"); }} style={th}>{t.colClient}{arrow("client")}</th>
                <th onClick={function () { toggleSort("type"); }} style={thC}>{t.colType}{arrow("type")}</th>
                <th onClick={function () { toggleSort("product"); }} style={th}>{t.colProduct}{arrow("product")}</th>
                <th style={thC}>{t.colCat}</th>
                <th onClick={function () { toggleSort("po"); }} style={th}>{t.colPO}{arrow("po")}</th>
                <th style={th}>{t.colPU}</th>
                <th style={thR}>{t.colPallets}</th>
                <th onClick={function () { toggleSort("cases"); }} style={thR}>{t.colCases}{arrow("cases")}</th>
                <th style={thR}>{t.colSalePrice}</th>
                <th style={thR}>{t.colGrowerPrice}</th>
                <th onClick={function () { toggleSort("revenue"); }} style={thR}>{t.colRevenue}{arrow("revenue")}</th>
                <th style={thR}>{t.colDiffProfit}</th>
                <th style={thR}>{t.colCommProfit}</th>
                <th style={thR}>{t.colFinanceProfit}</th>
                <th style={thR}>{t.colSLProfit}</th>
                <th onClick={function () { toggleSort("estProfit"); }} style={thR}>{t.colEstProfit}{arrow("estProfit")}</th>
                <th onClick={function () { toggleSort("realProfit"); }} style={thR}>{t.colRealProfit}{arrow("realProfit")}</th>
                <th onClick={function () { toggleSort("status"); }} style={thC}>{t.colSaleStatus}{arrow("status")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(function (r) {
                return (
                    <tr key={r.id} onClick={showReadOnlyMsg} style={{ cursor: "default", background: selected[r.id] ? "rgba(42,122,111,0.05)" : "transparent" }}
                        onMouseEnter={function (e) { if (!selected[r.id]) e.currentTarget.style.background = "var(--color-bg)"; }}
                        onMouseLeave={function (e) { if (!selected[r.id]) e.currentTarget.style.background = "transparent"; }}>
                      <td style={{ ...tdC, width: 32 }} onClick={function (e) { e.stopPropagation(); }}>
                        <input type="checkbox" checked={!!selected[r.id]} onChange={function (e) { var next = { ...selected }; if (e.target.checked) next[r.id] = true; else delete next[r.id]; setSelected(next); }} />
                      </td>
                      {/* Loading/Delivery first, Departure second. */}
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.deliveryDate || "—"}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.date || "—"}</td>
                      <td style={tdC}>{r.week ? "W" + r.week : "—"}</td>
                      <td style={td}>{r.clientName || "—"}</td>
                      <td style={tdC}><TypeBadge type={r.sourceType} t={t} /></td>
                      <td style={td}>{r.productDescription || "—"}</td>
                      <td style={tdC}>{r.category || "—"}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "var(--color-accent)" }}>{r.poNumber || "—"}</td>
                      <td style={{ ...td, fontFamily: "'JetBrains Mono', monospace" }}>{r.puNumber || "—"}</td>
                      <td style={tdR}>{r.pallets || "—"}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{r.cases || "—"}</td>
                      <td style={tdR}>{r.hasReal ? fmtMoneyShort(r.salePriceReal) : fmtMoneyShort(r.salePriceEst)}</td>
                      <td style={tdR}>{fmtMoneyShort(r.growerPrice)}</td>
                      <td style={{ ...tdR, fontWeight: 600 }}>{moneyCell(r.hasReal ? r.revenueReal : r.revenueEst)}</td>
                      <td style={tdR}>{moneyCell(r.profitDiff)}</td>
                      <td style={tdR}>{moneyCell(r.profitComm)}</td>
                      <td style={tdR}>{moneyCell(r.hasReal ? r.profitFinance_real : r.profitFinance)}</td>
                      <td style={tdR}>{r.sourceType === "fixed" ? moneyCell(r.hasReal ? r.profitSL_real : r.profitSL) : <span style={{ color: "var(--color-text-muted)" }}>—</span>}</td>
                      <td style={{ ...tdR, fontWeight: 700 }}>{moneyCell(r.estProfit, { emphasize: true })}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: r.hasReal ? "#047857" : "var(--color-text-muted)" }}>{r.hasReal ? moneyCell(r.realProfit, { emphasize: true }) : "—"}</td>
                      <td style={tdC}><StatusBadge status={r.saleStatus || "pending"} t={t} /></td>
                    </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Toast msg={toast} onDismiss={function () { setToast(""); }} />
    </div>
  );
}
